import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { PackingItemResponseDto, SuggestedPackingItemDto } from "@tabipla/shared/types";
import type { TripType } from "./constants";
import type { PackingProgress, PriorityFilter } from "./types";

// ==================== Context Value Type ====================

export interface PackingContextValue {
  // Data
  items: PackingItemResponseDto[];
  progress: PackingProgress | null;
  isLoading: boolean;

  // Computed
  priorityCounts: { all: number; high: number; medium: number; low: number; none: number };

  // Form State
  newItemName: string;
  onNewItemNameChange: (value: string) => void;
  onAddItem: (e: React.FormEvent) => void;
  isCreating: boolean;

  // Filter State
  showUncheckedOnly: boolean;
  priorityFilter: PriorityFilter;
  onToggleShowUncheckedOnly: () => void;
  onPriorityFilterChange: (filter: PriorityFilter) => void;

  // Edit Mode State
  isEditMode: boolean;
  selectedItemIds: Set<string>;
  onToggleEditMode: () => void;
  onToggleItemSelection: (itemId: string) => void;
  onSelectAllItems: () => void;
  onDeselectAllItems: () => void;
  onBulkDelete: () => void;
  onBulkToggleCheck: () => void;

  // Item Actions
  onToggleItem: (itemId: string) => void;
  onDeleteItem: (itemId: string) => void;
  onSetPriority: (itemId: string, priority: number | null) => void;
  isToggling: boolean;
  isDeleting: boolean;
  isSettingPriority: boolean;

  // AI Suggestions
  aiSuggestions: SuggestedPackingItemDto[];
  selectedSuggestions: Set<number>;
  showAiPanel: boolean;
  isAiLoading: boolean;
  isBulkCreating: boolean;
  onAiSuggest: () => void;
  onToggleSuggestion: (index: number) => void;
  onSelectAllSuggestions: () => void;
  onDeselectAllSuggestions: () => void;
  onAddSelectedSuggestions: () => void;
  onCloseAiPanel: () => void;

  // Templates
  templateItems: { name: string }[];
  selectedTemplateItems: Set<number>;
  showTemplatePanel: boolean;
  selectedTripType: TripType | null;
  onSelectTemplate: (tripType: TripType) => void;
  onToggleTemplateItem: (index: number) => void;
  onSelectAllTemplateItems: () => void;
  onDeselectAllTemplateItems: () => void;
  onAddSelectedTemplateItems: () => void;
  onCloseTemplatePanel: () => void;
}

// ==================== Context ====================

const PackingContext = createContext<PackingContextValue | null>(null);

// ==================== Provider ====================

interface PackingProviderProps {
  value: Omit<PackingContextValue, "priorityCounts">;
  children: ReactNode;
}

export function PackingProvider({ value, children }: PackingProviderProps) {
  // Calculate priority counts
  const priorityCounts = useMemo(() => {
    const counts = { all: value.items.length, high: 0, medium: 0, low: 0, none: 0 };
    for (const item of value.items) {
      if (item.priority === 3) counts.high++;
      else if (item.priority === 2) counts.medium++;
      else if (item.priority === 1) counts.low++;
      else counts.none++;
    }
    return counts;
  }, [value.items]);

  const contextValue = useMemo(() => ({ ...value, priorityCounts }), [value, priorityCounts]);

  return <PackingContext.Provider value={contextValue}>{children}</PackingContext.Provider>;
}

// ==================== Hook ====================

export function usePackingContext(): PackingContextValue {
  const context = useContext(PackingContext);
  if (!context) {
    throw new Error("usePackingContext must be used within a PackingProvider");
  }
  return context;
}

// ==================== Selector Hooks ====================

/**
 * Hook for form-related state and actions
 */
export function usePackingForm() {
  const ctx = usePackingContext();
  return {
    newItemName: ctx.newItemName,
    isCreating: ctx.isCreating,
    onNewItemNameChange: ctx.onNewItemNameChange,
    onAddItem: ctx.onAddItem,
  };
}

/**
 * Hook for filter-related state and actions
 */
export function usePackingFilters() {
  const ctx = usePackingContext();
  return {
    showUncheckedOnly: ctx.showUncheckedOnly,
    priorityFilter: ctx.priorityFilter,
    onToggleShowUncheckedOnly: ctx.onToggleShowUncheckedOnly,
    onPriorityFilterChange: ctx.onPriorityFilterChange,
  };
}

/**
 * Hook for edit mode state and actions
 */
export function usePackingEditMode() {
  const ctx = usePackingContext();
  return {
    isEditMode: ctx.isEditMode,
    selectedItemIds: ctx.selectedItemIds,
    onToggleEditMode: ctx.onToggleEditMode,
    onToggleItemSelection: ctx.onToggleItemSelection,
    onSelectAllItems: ctx.onSelectAllItems,
    onDeselectAllItems: ctx.onDeselectAllItems,
    onBulkDelete: ctx.onBulkDelete,
    onBulkToggleCheck: ctx.onBulkToggleCheck,
    isToggling: ctx.isToggling,
    isDeleting: ctx.isDeleting,
  };
}

/**
 * Hook for item list data and actions
 */
export function usePackingItems() {
  const ctx = usePackingContext();
  return {
    items: ctx.items,
    progress: ctx.progress,
    isLoading: ctx.isLoading,
    priorityCounts: ctx.priorityCounts,
    onToggleItem: ctx.onToggleItem,
    onDeleteItem: ctx.onDeleteItem,
    onSetPriority: ctx.onSetPriority,
    isToggling: ctx.isToggling,
    isDeleting: ctx.isDeleting,
    isSettingPriority: ctx.isSettingPriority,
  };
}

/**
 * Hook for AI suggestions state and actions
 */
export function usePackingAiSuggestions() {
  const ctx = usePackingContext();
  return {
    aiSuggestions: ctx.aiSuggestions,
    selectedSuggestions: ctx.selectedSuggestions,
    showAiPanel: ctx.showAiPanel,
    isAiLoading: ctx.isAiLoading,
    isBulkCreating: ctx.isBulkCreating,
    onAiSuggest: ctx.onAiSuggest,
    onToggleSuggestion: ctx.onToggleSuggestion,
    onSelectAllSuggestions: ctx.onSelectAllSuggestions,
    onDeselectAllSuggestions: ctx.onDeselectAllSuggestions,
    onAddSelectedSuggestions: ctx.onAddSelectedSuggestions,
    onCloseAiPanel: ctx.onCloseAiPanel,
  };
}

/**
 * Hook for template state and actions
 */
export function usePackingTemplates() {
  const ctx = usePackingContext();
  return {
    templateItems: ctx.templateItems,
    selectedTemplateItems: ctx.selectedTemplateItems,
    showTemplatePanel: ctx.showTemplatePanel,
    selectedTripType: ctx.selectedTripType,
    isBulkCreating: ctx.isBulkCreating,
    onSelectTemplate: ctx.onSelectTemplate,
    onToggleTemplateItem: ctx.onToggleTemplateItem,
    onSelectAllTemplateItems: ctx.onSelectAllTemplateItems,
    onDeselectAllTemplateItems: ctx.onDeselectAllTemplateItems,
    onAddSelectedTemplateItems: ctx.onAddSelectedTemplateItems,
    onCloseTemplatePanel: ctx.onCloseTemplatePanel,
  };
}

/**
 * Hook for progress header data
 */
export function usePackingProgress() {
  const ctx = usePackingContext();
  return {
    progress: ctx.progress,
    priorityCounts: ctx.priorityCounts,
    isAiLoading: ctx.isAiLoading,
    showUncheckedOnly: ctx.showUncheckedOnly,
    priorityFilter: ctx.priorityFilter,
    isEditMode: ctx.isEditMode,
    onAiSuggest: ctx.onAiSuggest,
    onToggleShowUncheckedOnly: ctx.onToggleShowUncheckedOnly,
    onPriorityFilterChange: ctx.onPriorityFilterChange,
    onToggleEditMode: ctx.onToggleEditMode,
  };
}
